import { type ClassValue, clsx } from 'clsx';
import { format, parseISO } from 'date-fns';
import { twMerge } from 'tailwind-merge';

export function camelCaseToTitleCase(str: string) {
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase());
}

export function snakeCaseToTitleCase(str: string) {
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase());
}

export function formatObjectForSlack(
  obj: Object,
  omit?: string[],
  format?: 'camel' | 'snake'
) {
  const set = new Set(omit);
  return Object.entries(obj)
    .filter(([key, value]) => !set.has(key) && value)
    .map(
      ([key, value]) =>
        `*${format === 'camel' ? camelCaseToTitleCase(key) : snakeCaseToTitleCase(key)}:* ${value}`
    )
    .join('\n');
}

export function capitalizeFirstLetter(str: string) {
  const lower = str.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function convertPhoneNumber(phone: string) {
  return phone.replace(/\D/g, '');
}

export function formatPhoneNumber(phoneNumber: string): string {
  // Check if the phone number starts with +1 and remove it if it does
  if (phoneNumber.startsWith('+1')) {
    return phoneNumber.slice(2); // Remove the first two characters (+1)
  }
  return phoneNumber; // Return as-is if no +1
}

/**
 * Formats a phone number in the display format: +1 (XXX) XXX-XXXX
 * @param phoneNumber - The phone number to format (can be in any format)
 * @returns The formatted phone number string
 */
export function formatPhoneNumberDisplay(phoneNumber: string): string {
  // Normalize the phone number to just digits
  const cleaned = phoneNumber.replace(/\D/g, '');

  // Handle different cases
  if (cleaned.length === 0) {
    return '';
  }

  // For US numbers (assuming 10 or 11 digits)
  if (cleaned.length >= 10) {
    // If 11 digits and starts with 1, format as +1 (XXX) XXX-XXXX
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      const areaCode = cleaned.substring(1, 4);
      const firstPart = cleaned.substring(4, 7);
      const secondPart = cleaned.substring(7, 11);
      return `+1 (${areaCode}) ${firstPart}-${secondPart}`;
    }

    // If 10 digits, assume US number and add +1
    if (cleaned.length === 10) {
      const areaCode = cleaned.substring(0, 3);
      const firstPart = cleaned.substring(3, 6);
      const secondPart = cleaned.substring(6, 10);
      return `+1 (${areaCode}) ${firstPart}-${secondPart}`;
    }
  }

  // For other cases, just return in a basic format with country code if available
  if (cleaned.length > 10) {
    // Assume first digits might be country code
    const countryCode = cleaned.substring(0, cleaned.length - 10);
    const remaining = cleaned.substring(cleaned.length - 10);

    if (remaining.length >= 10) {
      const areaCode = remaining.substring(0, 3);
      const firstPart = remaining.substring(3, 6);
      const secondPart = remaining.substring(6, 10);
      return `+${countryCode} (${areaCode}) ${firstPart}-${secondPart}`;
    }

    // If we can't parse it in the standard format
    return `+${countryCode} ${remaining}`;
  }

  // For shorter numbers, just return as is with dashes for readability
  return cleaned.replace(/(\d{3})(\d{1,})/, '$1-$2');
}

export function convertISODate(isoDate: string) {
  const date = parseISO(isoDate);
  const formattedDate = format(date, 'M/d/yyyy');
  return formattedDate;
}

export function convertNormalDate(date: Date) {
  const formattedDate = format(date, 'M/d/yyyy');
  return formattedDate;
}

export function isRelatedToString(input: string, related: string): boolean {
  // Step 1: Normalize the string
  const normalizedInput = input.toLowerCase().trim();

  // Step 2: Keyword Checking
  if (normalizedInput.includes(related)) {
    return true;
  }

  // // Step 3: Fuzzy Matching (optional)
  // // The threshold for fuzzy matching can be adjusted as per your requirements.
  // // In this case, if the string is a close match to "plumbing", it returns true.
  // if (fuzzysearch('plumbing', normalizedInput)) {
  //     return true;
  // }

  return false;
}

export function isEmergency(str: string) {
  return (
    ((str.toLowerCase().includes('emergency') &&
      !str.toLowerCase().includes('non')) ||
      str.toLowerCase().includes('yes') ||
      str.toLowerCase() === 'y') &&
    !str.toLowerCase().includes('no')
  );
}

const numberMapping: { [key: string]: string } = {
  one: '1',
  two: '2',
  three: '3',
  four: '4',
  five: '5',
  six: '6',
  seven: '7',
  'Customer does not know': 'Customer does not know',
  '1': '1',
  '2': '2',
  '3': '3',
  '4': '4',
  '5': '5',
  '6': '6',
  '7': '7',
};

export function translateNumber(word: string = ''): string {
  return numberMapping[word] || '1';
}

// Comprehensive mapping for all U.S. states and their common abbreviations
const stateAbbreviations: { [key: string]: string } = {
  ALABAMA: 'AL',
  AL: 'AL',
  ALASKA: 'AK',
  AK: 'AK',
  ARIZONA: 'AZ',
  AZ: 'AZ',
  ARKANSAS: 'AR',
  AR: 'AR',
  CALIFORNIA: 'CA',
  CA: 'CA',
  COLORADO: 'CO',
  CO: 'CO',
  CONNECTICUT: 'CT',
  CT: 'CT',
  'DISTRICT OF COLUMBIA': 'DC',
  'WASHINGTON DC': 'DC',
  DC: 'DC',
  DELAWARE: 'DE',
  DE: 'DE',
  FLORIDA: 'FL',
  FL: 'FL',
  GEORGIA: 'GA',
  GA: 'GA',
  HAWAII: 'HI',
  HI: 'HI',
  IDAHO: 'ID',
  ID: 'ID',
  ILLINOIS: 'IL',
  IL: 'IL',
  INDIANA: 'IN',
  IN: 'IN',
  IOWA: 'IA',
  IA: 'IA',
  KANSAS: 'KS',
  KS: 'KS',
  KENTUCKY: 'KY',
  KY: 'KY',
  LOUISIANA: 'LA',
  LA: 'LA',
  MAINE: 'ME',
  ME: 'ME',
  MARYLAND: 'MD',
  MD: 'MD',
  MASSACHUSETTS: 'MA',
  MA: 'MA',
  MICHIGAN: 'MI',
  MI: 'MI',
  MINNESOTA: 'MN',
  MN: 'MN',
  MISSISSIPPI: 'MS',
  MS: 'MS',
  MISSOURI: 'MO',
  MO: 'MO',
  MONTANA: 'MT',
  MT: 'MT',
  NEBRASKA: 'NE',
  NE: 'NE',
  NEVADA: 'NV',
  NV: 'NV',
  'NEW HAMPSHIRE': 'NH',
  NH: 'NH',
  'NEW JERSEY': 'NJ',
  NJ: 'NJ',
  'NEW MEXICO': 'NM',
  NM: 'NM',
  'NEW YORK': 'NY',
  NY: 'NY',
  'NORTH CAROLINA': 'NC',
  NC: 'NC',
  'NORTH DAKOTA': 'ND',
  ND: 'ND',
  OHIO: 'OH',
  OH: 'OH',
  OKLAHOMA: 'OK',
  OK: 'OK',
  OREGON: 'OR',
  OR: 'OR',
  PENNSYLVANIA: 'PA',
  PA: 'PA',
  'RHODE ISLAND': 'RI',
  RI: 'RI',
  'SOUTH CAROLINA': 'SC',
  SC: 'SC',
  'SOUTH DAKOTA': 'SD',
  SD: 'SD',
  TENNESSEE: 'TN',
  TN: 'TN',
  TEXAS: 'TX',
  TX: 'TX',
  UTAH: 'UT',
  UT: 'UT',
  VERMONT: 'VT',
  VT: 'VT',
  VIRGINIA: 'VA',
  VA: 'VA',
  WASHINGTON: 'WA',
  WA: 'WA',
  'WEST VIRGINIA': 'WV',
  WV: 'WV',
  WISCONSIN: 'WI',
  WI: 'WI',
  WYOMING: 'WY',
  WY: 'WY',
  ALBERTA: 'AB',
  AB: 'AB',
  'BRITISH COLUMBIA': 'BC',
  BC: 'BC',
  MANITOBA: 'MB',
  MB: 'MB',
  'NEW BRUNSWICK': 'NB',
  NB: 'NB',
  'NEWFOUNDLAND AND LABRADOR': 'NL',
  NL: 'NL',
  'NOVA SCOTIA': 'NS',
  NS: 'NS',
  ONTARIO: 'ON',
  ON: 'ON',
  'PRINCE EDWARD ISLAND': 'PE',
  PE: 'PE',
  QUEBEC: 'QC',
  QC: 'QC',
  SASKATCHEWAN: 'SK',
  SK: 'SK',
  'NORTHWEST TERRITORIES': 'NT',
  NT: 'NT',
  NUNAVUT: 'NU',
  NU: 'NU',
  YUKON: 'YT',
  YT: 'YT',
};

// Comprehensive mapping for all Australian states and their common abbreviations
// Reason: conflict between Washington (WA) and Western Australia (WA)
const australianStateAbbreviations: { [key: string]: string } = {
  'NEW SOUTH WALES': 'NSW',
  NSW: 'NSW',
  VICTORIA: 'VIC',
  VIC: 'VIC',
  QUEENSLAND: 'QLD',
  QLD: 'QLD',
  'SOUTH AUSTRALIA': 'SA',
  SA: 'SA',
  'WESTERN AUSTRALIA': 'WA',
  WA: 'WA',
  TASMANIA: 'TAS',
  TAS: 'TAS',
  'AUSTRALIAN CAPITAL TERRITORY': 'ACT',
  ACT: 'ACT',
  'NORTHERN TERRITORY': 'NT',
  NT: 'NT',
};

/**
 * @deprecated Consider using getStateAbbr instead
 * Utility function to get the standard state abbreviation
 */
export function getStandardStateAbbreviation(state: string): string {
  return getStateAbbr(state) || state;
}

/**
 * Get state abbreviation from some representation of the state name
 *
 * @param state - State name
 * @returns State abbreviation or null if not found
 */
export function getStateAbbr(state: Nullable<string>, country: string = 'US') {
  if (!state) {
    return null;
  }
  state = state.trim().toUpperCase();

  // Handle country-specific state abbreviations
  switch (country.toUpperCase()) {
    case 'AU':
      return australianStateAbbreviations[state] || null;
    default:
      return stateAbbreviations[state] || null;
  }
}

// Utility function to get the standard state abbreviation for Australia
export function getStandardAUStateAbbreviation(state: string): string {
  state = state.trim().toUpperCase(); // Normalize the input
  return australianStateAbbreviations[state] || state; // Return the abbreviation or the original string if not found
}

export function getFullStateName(abbreviation: string): string {
  const abbr = abbreviation.trim().toUpperCase(); // Normalize the input

  // Create a reverse mapping of abbreviations to full state names
  const reverseMapping: { [key: string]: string } = {};
  for (const [key, value] of Object.entries(stateAbbreviations)) {
    // Only map full state names to their abbreviations
    if (!reverseMapping[value] && key.length > 2) {
      reverseMapping[value] = key;
    }
  }

  return reverseMapping[abbr] || abbreviation; // Return the full state name or the original input if not found
}

export function levenshtein(a: string, b: string): number {
  const matrix: number[][] = [];

  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          Math.min(matrix[i][j - 1] + 1, matrix[i - 1][j] + 1)
        );
      }
    }
  }

  return matrix[b.length][a.length];
}

export function similarity(a: string, b: string): number {
  const distance = levenshtein(a, b);
  const maxLen = Math.max(a.length, b.length);
  return (1 - distance / maxLen) * 100;
}

export const isValidEmail = (associate: Associate): boolean => {
  return associate.email.includes('@') && associate.email !== 'unknown';
}; // if receive_email is True but email is unknown, Resend is not sending that. So filter out unknown here

export function isNumber(value: any): boolean {
  return !isNaN(Number(value));
}

export function isSubstringCaseless(
  substring: Nullable<string>,
  mainString: Nullable<string>
): boolean {
  if (!substring || !mainString) {
    return false;
  }
  return mainString.toLowerCase().includes(substring.toLowerCase());
}

/**
 * Utility function to find the item with a date closest to the target date
 * @param items Array of items to search through
 * @param targetDate The target date to compare against
 * @param getDate Function to extract the date from an item
 * @returns The item with the closest date or null if no valid items
 */
export function findItemWithClosestDate<T>(
  items: T[],
  targetDate: Date,
  getDate: (item: T) => Date | null
): T | null {
  if (!items || items.length === 0) {
    return null;
  }

  return items.reduce(
    (closest, current) => {
      const currentDate = getDate(current);
      if (!currentDate) {
        return closest;
      }

      const closestDate = closest ? getDate(closest) : null;

      // Calculate time difference in milliseconds between current item and target date
      const currentDiff = Math.abs(
        currentDate.getTime() - targetDate.getTime()
      );

      // Calculate time difference in milliseconds between closest item so far and target date
      const closestDiff = closestDate
        ? Math.abs(closestDate.getTime() - targetDate.getTime())
        : Infinity;

      // Return the item with the smaller time difference
      return currentDiff < closestDiff ? current : closest;
    },
    null as T | null
  );
}

export function formatFullName(
  firstName: Nullable<string>,
  lastName: Nullable<string>
): string {
  return `${firstName || ''} ${lastName || ''}`.trim();
}
